import type {Product} from '@shopify/hydrogen/storefront-api-types';
import {useRef, useEffect, useState} from 'react';
import Slider from '@moredeal/react-slick';
import {ProductCard, Heading, IconCarouselControl} from '~/components';

export function ProductCardCarousel({
  products,
  title,
  className,
}: {
  products: Product[];
  title: string;
  className?: string;
}) {
  const slider = useRef<any>(false);
  const sliderDom = useRef<any>();
  const [state, setState] = useState({
    prevHover: false,
    nextHover: false,
    activeSlide: 0,
    completionPercentage: 0,
  });

  useEffect(() => {
    setState({
      ...state,
      completionPercentage: ((state.activeSlide + 1) / products.length) * 100,
    });
  }, [state.activeSlide]);

  const sliderSettings = {
    slidesToShow: 4,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 7500,
    arrows: false,
    beforeChange: (current: number, next: number) =>
      setState({...state, activeSlide: next}),
    responsive: [
      {
        breakpoint: 1000,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 2,
        },
      },
      {
        breakpoint: 600,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
    ],
  };

  useEffect(() => {
    slider.current = sliderDom.current;
  }, []);

  const prevSlide = () => {
    if (slider.current) {
      slider.current.slickPrev();
    }
  };

  const nextSlide = () => {
    if (slider.current) {
      slider.current.slickNext();
    }
  };

  return (
    <div className="py-8 md:py-16">
      <div className="flex justify-between pb-4 lg:pb-16">
        {title && <Heading size="lead">{title}</Heading>}
        <div className="grid gap-2 grid-flow-col">
          <IconCarouselControl
            direction="left"
            className="cursor-pointer"
            filled={state.prevHover}
            onMouseLeave={() => setState({...state, prevHover: false})}
            onMouseOver={() => setState({...state, prevHover: true})}
            onClick={prevSlide}
          />
          <IconCarouselControl
            direction="right"
            className="cursor-pointer"
            filled={state.nextHover}
            onMouseLeave={() => setState({...state, nextHover: false})}
            onMouseOver={() => setState({...state, nextHover: true})}
            onClick={nextSlide}
          />
        </div>
      </div>

      <div className="-mx-3">
        <Slider {...sliderSettings} ref={sliderDom}>
          {products.map((product) => (
            <ProductCard product={product} key={product.id} className="px-3" />
          ))}
        </Slider>
      </div>

      <div className="w-2/3 lg:w-1/6 mx-auto flex mt-10 relative">
        <div className="border-divider border-b-4 w-full"></div>
        <div
          className="border-accentDarkRed border-b-4 absolute top-0 left-0 transition-all duration-100 ease-in-out"
          style={{width: state.completionPercentage + '%'}}
        ></div>
      </div>
    </div>
  );
}
